import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

/**
 * Self-service "My Profile" surface for staff users.
 *
 * Layout:
 *   1. Approval status banner (pending vs approved vs rejected).
 *   2. Read-only admin-controlled fields (hourly rate, contract hours,
 *      competencies). These are set by an admin — surfaced here so staff
 *      can see what's been agreed.
 *   3. Editable form (whitelisted fields only):
 *        phone, address, emergency contact, qualifications, NI number.
 *
 * The backend enforces the same whitelist, so any sneak attempt is ignored.
 */
export function StaffProfileTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    phone: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    qualifications: '',
    ni_number: '',
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/staff/profiles/me`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed to load profile' }));
        throw new Error(err.detail || 'Failed to load profile');
      }
      const data = await res.json();
      setProfile(data);
      setForm({
        phone: data.phone || '',
        address: data.address || '',
        emergency_contact_name: data.emergency_contact_name || '',
        emergency_contact_phone: data.emergency_contact_phone || '',
        qualifications: data.qualifications || '',
        ni_number: data.ni_number || '',
      });
    } catch (err) {
      setError(err.message || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(
        `${import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/staff/profiles/me`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Save failed' }));
        throw new Error(err.detail || 'Save failed');
      }
      const data = await res.json();
      setProfile(data);
      setSuccess('Profile saved. ' + (data.is_active
        ? ''
        : 'An admin will review your changes shortly.'));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="card">Loading profile…</div>;
  if (error && !profile) {
    return <div className="card" style={{ color: '#9f1239' }}>Error: {error}</div>;
  }
  if (!profile) return null;

  const status = profile.approval_status || (profile.is_active ? 'approved' : 'pending');

  return (
    <>
      <div className="page-header">
        <h1>👤 My Profile</h1>
        <p>Keep your contact and qualification details up to date.</p>
      </div>

      {/* Approval status banner */}
      <div
        className="card"
        data-testid="staff-approval-banner"
        style={{
          marginBottom: '1.25rem',
          background: status === 'approved'
            ? 'hsl(142 50% 95%)'
            : status === 'rejected'
              ? 'hsl(0 70% 96%)'
              : 'hsl(45 95% 94%)',
          borderColor: status === 'approved'
            ? 'hsl(142 60% 70%)'
            : status === 'rejected'
              ? 'hsl(0 70% 75%)'
              : 'hsl(45 95% 65%)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0 }}>
              {status === 'approved' && '✅ Approved'}
              {status === 'pending'  && '⏳ Pending admin approval'}
              {status === 'rejected' && '❌ Rejected'}
            </h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
              {status === 'approved'
                ? 'You\'re fully onboarded. Your shifts and timesheet data are now active.'
                : status === 'rejected'
                  ? 'Your profile was rejected. Please contact your admin for next steps.'
                  : 'Your profile has been submitted. An admin will review and activate your account so you can be added to shifts.'}
            </p>
          </div>
          <span className="badge" style={{
            background: '#fff',
            border: '1px solid hsl(var(--border))',
            padding: '0.3rem 0.6rem',
            fontSize: '0.75rem',
          }}>
            Account: {profile.email}
          </span>
        </div>
      </div>

      {/* Read-only admin-set fields */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ marginTop: 0 }}>🔒 Set by your admin</h3>
        <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
          These fields are managed by your administrator and cannot be edited from here.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>Hourly rate</small>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }} data-testid="profile-hourly-rate">
              £{(profile.hourly_rate ?? 0).toFixed(2)}
            </div>
          </div>
          <div>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>Contract hours / week</small>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }} data-testid="profile-contract-hours">
              {profile.contract_hours ?? 0} h
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>Competencies</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.25rem' }} data-testid="profile-competencies">
              {(profile.competencies || []).length === 0 && (
                <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
                  None assigned yet
                </span>
              )}
              {(profile.competencies || []).map((c, i) => (
                <span key={i} className="badge badge-blue" style={{ fontSize: '0.8rem' }}>
                  {c === 'first_aider' ? '🩹 First Aider'
                    : c === 'level_3_qualified' ? '🎓 Level 3'
                    : c === 'driver' ? '🚗 Driver'
                    : c === 'senco' ? '👨‍🏫 SENCO'
                    : c === 'safeguarding_lead' ? '🛡️ Safeguarding Lead'
                    : c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Editable self-service form */}
      <form onSubmit={handleSave} className="card" data-testid="staff-self-form">
        <h3 style={{ marginTop: 0 }}>✏️ My details</h3>
        <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
          Edit anything in this section to update your profile. Your admin
          will be notified and may need to re-approve significant changes.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="profile-phone-input"
              placeholder="+44 ..."
            />
          </div>
          <div className="form-group">
            <label>NI number</label>
            <input
              type="text" value={form.ni_number}
              onChange={(e) => setForm({ ...form, ni_number: e.target.value })}
              data-testid="profile-ni-input"
              placeholder="QQ 12 34 56 C"
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Address</label>
            <input
              type="text" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="profile-address-input"
              placeholder="123 High Street, London, SW1A 1AA"
            />
          </div>
          <div className="form-group">
            <label>Emergency contact name</label>
            <input
              type="text" value={form.emergency_contact_name}
              onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
              data-testid="profile-emergency-name-input"
            />
          </div>
          <div className="form-group">
            <label>Emergency contact phone</label>
            <input
              type="tel" value={form.emergency_contact_phone}
              onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
              data-testid="profile-emergency-phone-input"
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Qualifications</label>
            <textarea
              rows={3}
              value={form.qualifications}
              onChange={(e) => setForm({ ...form, qualifications: e.target.value })}
              data-testid="profile-qualifications-input"
              placeholder="e.g. NVQ Level 3 in Childcare, Paediatric First Aid (2024), Safeguarding Level 2..."
            />
          </div>
        </div>

        {error && (
          <div data-testid="profile-error"
            style={{
              marginTop: '1rem',
              padding: '0.6rem 0.85rem',
              background: '#fff1f2',
              border: '1px solid #fecdd3',
              borderRadius: 'var(--radius)',
              fontSize: '0.85rem',
              color: '#9f1239',
            }}>{error}</div>
        )}
        {success && (
          <div data-testid="profile-success"
            style={{
              marginTop: '1rem',
              padding: '0.6rem 0.85rem',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 'var(--radius)',
              fontSize: '0.85rem',
              color: '#166534',
            }}>{success}</div>
        )}

        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            type="button" className="btn btn-ghost"
            onClick={() => load()} disabled={saving}
            data-testid="profile-reset-btn"
          >
            Reset
          </button>
          <button
            type="submit" className="btn btn-primary"
            disabled={saving}
            data-testid="profile-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </>
  );
}

export default StaffProfileTab;
